import { useEffect, useState } from "react";
import { api } from "../api/client";
import { useRole } from "../context/RoleContext";
import { useAuth } from "../context/AuthContext";
import { useT } from "../i18n";
import { formatDate } from "../utils/format";
export function MeetingsPanel({
  processId,
  code,
  history,
}: {
  processId: number;
  code: string;
  history: any[];
}) {
  const { t, lang } = useT();
  const { isCde, isGovernance } = useRole();
  const { user } = useAuth();
  const [meetings, setMeetings] = useState<any[]>([]),
    [connection, setConnection] = useState<any>({}),
    [error, setError] = useState(""),
    [message, setMessage] = useState(""),
    [busy, setBusy] = useState(false),
    [add, setAdd] = useState(false),
    [token, setToken] = useState("");
  const [form, setForm] = useState({
    title: "",
    starts_at: "",
    participants: "",
    minutes: "",
    recording_url: "",
    held: false,
  });
  const load = () =>
    Promise.all([
      api.getMeetings(processId).then(setMeetings),
      api.getMeetingConnection().then(setConnection),
    ]);
  useEffect(() => {
    load().catch((e) => setError(e.message));
  }, [processId]);
  const act = async (fn: () => Promise<any>) => {
    setBusy(true);
    setError("");
    setMessage("");
    try {
      await fn();
      await load();
    } catch (e: any) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };
  return (
    <>
      <p className="text-xs text-ink-3">{t("control.syncHint", { code })}</p>
      {error && (
        <p className="text-err text-sm" role="alert">
          {error}
        </p>
      )}
      {message && (
        <p className="text-sm" role="status">
          {message}
        </p>
      )}
      {(isCde || isGovernance) && (
        <div className="flex gap-8 mt-12 mb-16 control-wrap">
          <button
            className="btn btn-sm"
            disabled={busy}
            onClick={() =>
              act(async () => {
                const result = await api.syncMeetings();
                setMessage(
                  Object.entries(result)
                    .map(
                      ([key, v]: any) =>
                        `${t(`control.${key}`)} : ${v.error || t("control.imported", { n: v.count })}`,
                    )
                    .join(" · "),
                );
              })
            }
          >
            {busy ? t("common.loading") : t("control.sync")}
          </button>
          {isCde && (
            <button className="btn btn-sm" onClick={() => setAdd(!add)}>
              {t("control.addMeeting")}
            </button>
          )}
        </div>
      )}
      {add && (
        <form
          className="card"
          onSubmit={(e) => {
            e.preventDefault();
            act(async () => {
              await api.addMeeting(processId, {
                ...form,
                starts_at: new Date(form.starts_at).toISOString(),
              });
              setAdd(false);
              setForm({
                title: "",
                starts_at: "",
                participants: "",
                minutes: "",
                recording_url: "",
                held: false,
              });
            });
          }}
        >
          {(
            ["title", "starts_at", "participants", "recording_url"] as const
          ).map((key) => (
            <label className="form-group" key={key}>
              <span className="form-label">
                {t(
                  `control.${({ title: "meetingTitle", starts_at: "meetingDate", participants: "participants", recording_url: "recording" } as const)[key]}`,
                )}
              </span>
              <input
                required={key === "title" || key === "starts_at"}
                type={
                  key === "starts_at"
                    ? "datetime-local"
                    : key === "recording_url"
                      ? "url"
                      : "text"
                }
                className="form-input"
                value={form[key]}
                onChange={(e) => setForm({ ...form, [key]: e.target.value })}
              />
            </label>
          ))}
          <label className="form-group">
            <span className="form-label">{t("control.minutes")}</span>
            <textarea
              className="form-textarea"
              rows={4}
              value={form.minutes}
              onChange={(e) => setForm({ ...form, minutes: e.target.value })}
            />
          </label>
          <label className="text-sm">
            <input
              type="checkbox"
              checked={form.held}
              onChange={(e) => setForm({ ...form, held: e.target.checked })}
            />{" "}
            {t("control.held")}
          </label>
          <div className="mt-12">
            <button className="btn btn-accent btn-sm" disabled={busy}>
              {t("common.save")}
            </button>
          </div>
        </form>
      )}
      {!meetings.length && (
        <p className="text-sm text-ink-3">{t("control.noMeetings")}</p>
      )}
      {meetings.map((m) => (
        <article className="seance-card control-meeting" key={m.id}>
          <div className="flex justify-between gap-8">
            <strong>{m.title}</strong>
            <span className="badge">
              {t(
                m.cancelled
                  ? "control.cancelled"
                  : m.held || m.minutes || m.transcript
                    ? "control.documented"
                    : new Date(m.starts_at) > new Date()
                      ? "control.scheduled"
                      : "control.pastEvent",
              )}
            </span>
          </div>
          <p className="text-xs text-ink-3">
            {formatDate(m.starts_at, lang)} ·{" "}
            {new Date(m.starts_at).toLocaleTimeString(lang, {
              hour: "2-digit",
              minute: "2-digit",
            })}
            {m.participants && ` · ${m.participants}`}
          </p>
          <p className="text-xs text-ink-3">
            {m.is_shared ? t("control.shared") : t("control.private")}
          </p>
          {m.minutes && <p className="control-note-body">{m.minutes}</p>}
          {m.transcript && (
            <details>
              <summary>{t("control.transcript")}</summary>
              <p className="control-note-body">{m.transcript}</p>
            </details>
          )}
          <div className="flex gap-8 control-wrap">
            {m.recording_url && (
              <a
                className="btn btn-sm"
                href={m.recording_url}
                target="_blank"
                rel="noreferrer"
              >
                {t("control.recording")}
              </a>
            )}
            {m.join_url && !m.cancelled && (
              <a
                className="btn btn-sm"
                href={m.join_url}
                target="_blank"
                rel="noreferrer"
              >
                {t("control.join")}
              </a>
            )}
            {(isCde || isGovernance) &&
              !m.held &&
              new Date(m.starts_at) <= new Date() && (
                <button
                  className="btn btn-sm"
                  disabled={busy}
                  onClick={() =>
                    act(() => api.updateMeeting(m.id, { held: true }))
                  }
                >
                  {t("control.confirmHeld")}
                </button>
              )}
            {m.created_by === user?.id && !m.is_shared && (
              <button
                className="btn btn-sm"
                disabled={busy}
                onClick={() =>
                  act(() => api.updateMeeting(m.id, { is_shared: true }))
                }
              >
                {t("control.shareMeeting")}
              </button>
            )}
          </div>
          <label className="form-group mt-12">
            <span className="form-label">{t("control.association")}</span>
            <select
              className="form-select"
              disabled={busy || !(isCde || isGovernance)}
              value={m.process_id ?? ""}
              onChange={(e) =>
                act(() =>
                  api.updateMeeting(m.id, {
                    process_id: Number(e.target.value) || null,
                  }),
                )
              }
            >
              <option value="">{t("control.unlinked")}</option>
              {history.map((h) => (
                <option key={h.id} value={h.id}>
                  {h.target_version || h.name} ·{" "}
                  {formatDate(h.created_at, lang)}
                </option>
              ))}
            </select>
          </label>
        </article>
      ))}
      {(isCde || isGovernance) && (
        <details className="mt-16">
          <summary>{t("control.linkSpeakr")}</summary>
          <p className="text-xs text-ink-3 mt-12">
            {t("control.calendar")} :{" "}
            {connection.calendar_configured ? "✓" : t("control.notConfigured")}
            <br />
            {t("control.speakr")} :{" "}
            {connection.speakr_linked ? "✓" : t("control.notConfigured")}
          </p>
          {connection.speakr_configured && (
            <form
              onSubmit={(e) => {
                e.preventDefault();
                act(async () => {
                  await api.connectSpeakr(token);
                  setToken("");
                });
              }}
            >
              <label className="form-group">
                <span className="form-label">{t("control.token")}</span>
                <input
                  type="password"
                  autoComplete="off"
                  className="form-input"
                  value={token}
                  onChange={(e) => setToken(e.target.value)}
                />
              </label>
              <button className="btn btn-sm" disabled={busy || !token.trim()}>
                {t("control.linkSpeakr")}
              </button>
            </form>
          )}
        </details>
      )}
    </>
  );
}
